import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'

import { BehaviorSubject, Observable, throwError } from 'rxjs'
import { catchError, flatMap, map, tap } from 'rxjs/operators'

import { ContentType, KeyNodeTitle } from '@overtur-monorepo/keying-shared'
import { MasterKey } from '@overtur-monorepo/keying-hierarchy'
import { KeyingAppEnvironmentConfig } from '../configuration/keying.app.environment.config'
import { of } from 'rxjs'
import { throttleJoin } from '../functions/throttleJoin'
import { LoadingService } from './loading.service'
import { KeyingDataSetsService } from './keying.data.sets.service'
import { ErrorService } from './error.service'
import { LoggingService } from './logging.service'

@Injectable({
  providedIn: 'root',
})
export class MasterKeyService {
  constructor(
    private keyingAppEnvironmentConfig: KeyingAppEnvironmentConfig,
    private loadingService: LoadingService,
    private errorService: ErrorService,
    @Inject(HttpClient) private httpClient: HttpClient,
    private loggingService: LoggingService
  ) {}

  masterKeyUpdatedSubject = new BehaviorSubject<any>({})
  masterKeyUpdated$ = this.masterKeyUpdatedSubject.asObservable()

  getMasterKeys(structureId, keySystemId, budget): Observable<MasterKey> {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/keySystems/${keySystemId}/MasterKeys`

    let type

    switch (budget) {
      case true:
        type = 'application/allegion.keying.masterkey.budget+json'
        break
      default:
        type = 'application/json'
        break
    }

    let headers = new HttpHeaders().set('Accept', type)

    return this.httpClient.get(url, { headers }).pipe(
      map((res) => new MasterKey(res)),
      catchError((error) => {
        return throwError('Something went wrong!')
      })
    )
  }

  getBudgetMasterKeys(structureId: string | number, keySystemId: string | number): Observable<MasterKey> {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/keySystems/${keySystemId}/MasterKeys`
    const headers = new HttpHeaders().set('Accept', 'application/allegion.keying.masterkey.budget+json')

    return this.httpClient.get(url, { headers }).pipe(
      map((res) => new MasterKey(res)),
      catchError((error) => {
        console.error('HTTP error:', error)
        return throwError(() => error)
      })
    )
  }

  getHierarchyMasterKeys(structureId: string | number, keySystemId: string | number): Observable<MasterKey> {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/keySystems/${keySystemId}/keys/hierarchy`
    // const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/keySystems/${keySystemId}/MasterKeys`

    const headers = new HttpHeaders().set('Accept', 'application/json')

    return this.httpClient.get(url, { headers }).pipe(
      map((res) => new MasterKey(res)),
      catchError((error) => {
        console.error('HTTP error:', error)
        return throwError(() => error)
      })
    )
  }

  getLastCrossKey(structureId, keySystemId) {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/keySystems/${keySystemId}/MasterKeys`
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/allegion.keying.get.lastcreated.crosskey+json',
      }),
    }
    return this.httpClient.get<any>(url, httpOptions)
  }

  getLastSKD(structureId, keySystemId) {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/keySystems/${keySystemId}/MasterKeys`
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/allegion.keying.get.lastcreated.singlekeydifferent+json',
      }),
    }
    return this.httpClient.get<any>(url, httpOptions)
  }

  postMasterKeys(masterKeys, structureId, keySystemId) {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/keySystems/${keySystemId}/MasterKeys`
    return this.httpClient.post<any>(url, masterKeys)
  }

  deleteMasterKeys(structureId, keySystemId, keyId, action = 'master', auditTrailRequest: any = undefined) {
    let url
    let type

    switch (action) {
      case 'crossKey':
        type = 'application/allegion.keying.addcrosskey+json'
        url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/keySystems/${keySystemId}/MasterKeys/${keyId}`
        break
      default:
        type = 'application/json'
        url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/keySystems/${keySystemId}/Keys/${keyId}`
        break
    }

    const headers = new HttpHeaders().set('Content-Type', type)
    return this.httpClient.delete<any>(url, { headers, body: { auditTrailRequest: auditTrailRequest || {} } })
  }

  deleteManyMasterKeys(structureId, keySystemId, masterKeys) {
    let observables = masterKeys.map((masterKey) => {
      return this.deleteMasterKeys(structureId, keySystemId, masterKey.id).pipe(
        catchError((err) => {
          return of()
        })
      )
    })
    return throttleJoin(observables, 25).pipe(
      flatMap((results) => {
        return results
      })
    )
  }

  deleteKeysThruNotation(structureId, keySystemId, masterKeyId, data) {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/keySystems/${keySystemId}/MasterKeys/${masterKeyId}`
    let type

    type = 'application/allegion.keying.deletechangekeys+json'

    const body = {
      prefix: data.prefix,
      from: data.from,
      to: data.to,
      suffix: data.suffix,
      allowAllCharacters: data.allowAllCharacters,
    }

    let headers = new HttpHeaders().set('Content-Type', type)
    return this.httpClient.put<any>(url, body, { headers })
  }

  patchMasterKeys(changes, structureId, keySystemId, masterKeyId) {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/keySystems/${keySystemId}/MasterKeys/${masterKeyId}`
    return this.httpClient.patch<any>(url, changes)
  }

  patchConstructionKey(changes, structureId, keySystemId, masterKeyId) {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/keySystems/${keySystemId}/MasterKeys/${masterKeyId}`
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/allegion.keying.updateconstructionkey+json',
      }),
    }
    return this.httpClient.patch<any>(url, changes, httpOptions)
  }

  moveKeys(structureId, keySystemId, data) {
    this.loadingService.set({ isLoading: true, message: `Moving Key(s)` })
    const moveURL = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/keySystems/${keySystemId}/MasterKeys`
    const type = ContentType.MOVE_KEYS
    const body = {
      keySetSymbols: data.keySetSymbols,
      prefix: data.prefix,
      from: data.from,
      to: data.to,
      suffix: data.suffix,
      targetKeySystemName: data.targetKeySystemName,
      masterKeySetSymbol: data.masterKeySetSymbol,
      keySectionId: data.keySectionId,
      tmkBitting: data.tmkBitting,
    }
    this.loggingService.log('moved keys data: ', data)

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': type,
      }),
    }
    return this.httpClient.put<any>(moveURL, body, httpOptions).pipe(
      tap(
        (resp) => {
          this.masterKeyUpdatedSubject.next({ data: { ...data, ...resp } })
          this.loadingService.set({ isLoading: false, message: `` })
        },
        (error) => {
          this.loadingService.set({ isLoading: false, message: `` })
          this.errorService.throw(error)
        }
      )
    )
  }

  renameKeys(structureId, keySystemId, data) {
    this.loadingService.set({ isLoading: true, message: `Renaming Key(s)` })
    const moveURL = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/keySystems/${keySystemId}/Keys/rename`
    const type = ContentType.RENAME_KEYS

    const body = data
    this.loggingService.log('rename keys data: ', data)
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': type,
      }),
    }
    return this.httpClient.put<any>(moveURL, body, httpOptions).pipe(
      tap(
        (resp) => {
          this.masterKeyUpdatedSubject.next({ data: { ...data, ...resp } })
          this.loadingService.set({ isLoading: false, message: `` })
        },
        (error) => {
          this.loadingService.set({ isLoading: false, message: `` })
          this.errorService.throw(error)
        }
      )
    )
  }

  changeKeyType(structureId, keySystemId, data) {
    this.loadingService.set({ isLoading: true, message: `Changing Key Type(s)` })
    const moveURL = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/keySystems/${keySystemId}/Keys`
    const type = ContentType.CHANGE_KEY_TYPE
    const body = {
      keySetSymbols: data.keySetSymbols,
      prefix: data.prefix,
      from: data.from,
      to: data.to,
      suffix: data.suffix,
      masterKeyTypeId: data.masterKeyTypeId,
    }
    this.loggingService.log('change key type data: ', data)

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': type,
      }),
    }
    return this.httpClient.put<any>(moveURL, body, httpOptions).pipe(
      tap(
        (resp) => {
          this.masterKeyUpdatedSubject.next({ data: { ...data, ...resp } })
          this.loadingService.set({ isLoading: false, message: `` })
          this.errorService.confirm('Key(s) Type have been updated.')
        },
        (error) => {
          this.loadingService.set({ isLoading: false, message: `` })
          this.errorService.throw(error)
        }
      )
    )
  }

  putMasterKeys(structureId, keySystemId, keyId, data) {
    let url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/keySystems/${keySystemId}/MasterKeys/${keyId}`
    let type = ''
    let body: any

    switch (data.action) {
      case KeyNodeTitle.SKD:
        body = {
          isFutureKey: data.isFutureKey,
        }
        type = ContentType.SKD
        break
      case KeyNodeTitle.DIRECT_CHANGES_NODE:
        body = {
          isFutureKey: data.isFutureKey,
        }
        type = ContentType.DIRECT_CHANGES
        break
      case KeyNodeTitle.CHANGE_KEYS:
        body = {
          prefix: data.prefix,
          from: data.from,
          to: data.to,
          suffix: data.suffix,
          allowAllCharacters: data.allowAllCharacters,
        }
        type = ContentType.CHANGE_KEYS
        break
      case KeyNodeTitle.SLM:
        body = {
          name: data.name,
          isFutureKey: data.isFutureKey,
        }
        type = ContentType.SLM
        break
      case KeyNodeTitle.CONTROL_KEYS:
        body = {
          name: data.name,
        }
        type = ContentType.CONTROL_KEYS
        break
      case 'constructionKey':
        type = ContentType.CONSTRUCTION_KEYS
        break
      case 'cross':
        body = {
          name: data.name,
        }
        type = ContentType.CROSS_KEYS
        url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/keySystems/${keySystemId}/keys/${keyId}`
        console.log(url)
        break
      case 'CROSS_KEY_RANGE':
        body = {
          prefix: data.prefix,
          from: data.from,
          to: data.to,
          suffix: data.suffix,
        }
        type = ContentType.CROSS_KEY_RANGE
        url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/keySystems/${keySystemId}/keys/${keyId}/bulk`
        break
      default:
        type = ContentType.MASTER_KEYS
        body = {
          prefix: data.prefix,
          from: data.from,
          to: data.to,
          suffix: data.suffix,
          allowAllCharacters: data.allowAllCharacters,
          isFutureKey: data.isFutureKey,
        }
        break
    }

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': type,
      }),
    }
    this.loggingService.log('body', body)
    return this.httpClient.put<any>(url, body, httpOptions).pipe(
      tap((resp) => {
        this.masterKeyUpdatedSubject.next({ data: { ...data, ...resp } })
      })
    )
  }

  postMasterKeysNotes(structureId, keySystemId, masterKeyId, note) {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/keySystems/${keySystemId}/MasterKeys/${masterKeyId}/Notes`
    return this.httpClient.post<any[]>(url, note)
  }

  patchDummyTmk(structureId, keySystemId, masterKeyId, request) {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/keySystems/${keySystemId}`
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/allegion.keying.activate.deactivate.tmk+json',
      }),
    }
    return this.httpClient.patch<any[]>(url, request, httpOptions)
  }
}
